import { FC, useState } from "react";
import { Button, Empty, Popconfirm, Card, Tag, Collapse } from "antd";
import { Plus, Edit, Trash2, Calendar, Clock, Users, Truck, DollarSign, Package, Copy, CalendarDays } from "lucide-react";
import dayjs from "dayjs";
import { useEstimateDetailStore } from "../estimate-detail.store";
import { useDeleteEstimateService, useAddEstimateService } from "~/services/estimate";
import { useNotification } from "~/hooks";
import { EstimateStatus, EstimateTicket, EstimateService } from "~/types/estimate";
import { DuplicateEstimateServiceModal, DuplicateEstimateServiceOptions } from "../duplicate-estimate-service-modal";
import { ExpoServiceForm } from "../expo-service-form";
import { ServiceType } from "~/utils/constants";

interface ServicesTabProps {
  estimate?: EstimateTicket;
}

export const ServicesTab: FC<ServicesTabProps> = ({ estimate }) => {
  const { openAddService, openEditService } = useEstimateDetailStore();
  const deleteServiceMutation = useDeleteEstimateService();
  const addServiceMutation = useAddEstimateService();
  const { success, error } = useNotification();

  // Duplicate modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [serviceToDuplicate, setServiceToDuplicate] = useState<EstimateService | null>(null);

  // Expo mode state - track which services have expo form expanded
  const [expandedExpoForms, setExpandedExpoForms] = useState<Set<string>>(new Set());

  if (!estimate) return null;

  // Check if expo mode is enabled (estimate has a project)
  const isExpoMode = !!estimate.projectId;

  // Toggle expo form expansion for a service
  const toggleExpoForm = (serviceId: string) => {
    const newExpanded = new Set(expandedExpoForms);
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
    }
    setExpandedExpoForms(newExpanded);
  };

  // Check if service is eligible for expo mode (ON_SITE_SERVICE)
  const isExpoEligible = (service: EstimateService) => {
    return isExpoMode && service.serviceType === ServiceType.ON_SITE_SERVICE;
  };

  const canEdit = estimate.status !== EstimateStatus.CONVERTED;

  const handleDelete = async (serviceId: string) => {
    try {
      await deleteServiceMutation.mutateAsync({ serviceId, estimateId: estimate.id });
      success({ message: "Service deleted successfully" });
    } catch (err: any) {
      error({ message: err.message || "Failed to delete service" });
    }
  };

  const handleOpenDuplicateModal = (service: EstimateService) => {
    setServiceToDuplicate(service);
    setDuplicateModalOpen(true);
  };

  const handleDuplicateService = async (options: DuplicateEstimateServiceOptions) => {
    if (!serviceToDuplicate || !estimate) return;

    const { includeVessel, includeInventoryTemplates, serviceDate } = options;

    try {
      const payload: any = {
        estimateTicketId: estimate.id,
        serviceType: serviceToDuplicate.serviceType,
        serviceDate: serviceDate ? serviceDate.toISOString() : undefined,
        duration: serviceToDuplicate.duration,
        staffCount: serviceToDuplicate.staffCount,
        notes: serviceToDuplicate.notes,
        estimatedItemCount: serviceToDuplicate.estimatedItemCount,
      };

      // Include vessel if selected
      if (includeVessel && serviceToDuplicate.vesselId) {
        payload.vesselId = serviceToDuplicate.vesselId;
      }

      // Include inventory templates if selected
      if (includeInventoryTemplates && serviceToDuplicate.inventoryTemplates?.length) {
        payload.inventoryTemplateIds = serviceToDuplicate.inventoryTemplates.map(t => t.id);
      }

      // Copy location data
      if (serviceToDuplicate.locationId) {
        payload.locationId = serviceToDuplicate.locationId;
      } else if (serviceToDuplicate.tempLocationData) {
        payload.tempLocationData = serviceToDuplicate.tempLocationData;
      }

      await addServiceMutation.mutateAsync(payload);
      success({ message: "Service duplicated successfully!" });

      // Close modal and reset state
      setDuplicateModalOpen(false);
      setServiceToDuplicate(null);
    } catch (err: any) {
      console.error("Duplicate service error:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to duplicate service";
      error({ message: errorMessage });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Estimate Services</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={openAddService}
          >
            Add Service
          </Button>
        )}
      </div>

      {estimate.services && estimate.services.length > 0 ? (
        <div className="space-y-4">
          {estimate.services.map((service) => {
            if (!service.id) {
              return null;
            }

            return (
            <Card key={service.id} className="shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="mb-3 flex items-center space-x-3">
                    <h3 className="text-lg font-medium capitalize">
                      {service.serviceType?.toLowerCase().replace('_', ' ') || 'Unknown Service'}
                    </h3>
                    {isExpoEligible(service) && (
                      <Tag color="purple" icon={<CalendarDays size={12} />}>
                        Expo Mode Available
                      </Tag>
                    )}
                    {service.expoDays && service.expoDays.length > 0 && (
                      <Tag color="green">
                        {service.expoDays.length} day{service.expoDays.length !== 1 ? 's' : ''} configured
                      </Tag>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {service.serviceDate && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar size={16} className="text-blue-500" />
                        <span>{dayjs(service.serviceDate).format("MMM DD, YYYY")}</span>
                      </div>
                    )}
                    {service.duration && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock size={16} className="text-purple-500" />
                        <span>{service.duration} hours</span>
                      </div>
                    )}
                    {service.staffCount && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Users size={16} className="text-orange-500" />
                        <span>{service.staffCount} staff</span>
                      </div>
                    )}
                    {service.vessel && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Truck size={16} className="text-green-500" />
                        <span>{service.vessel.name}</span>
                      </div>
                    )}
                    {service.estimatedItemCount && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Package size={16} className="text-indigo-500" />
                        <span>~{service.estimatedItemCount} items</span>
                      </div>
                    )}
                  </div>

                  {service.inventoryTemplates && service.inventoryTemplates.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-sm text-gray-500 mb-2">Inventory Templates:</div>
                      <div className="flex flex-wrap gap-2">
                        {service.inventoryTemplates.map((template) => (
                          <div
                            key={template.id}
                            className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs flex items-center space-x-1"
                          >
                            <Package size={12} />
                            <span>{template.name}</span>
                            {template.length && template.width && template.height && (
                              <span className="text-blue-500">
                                ({template.length}×{template.width}×{template.height})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {service.notes && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-sm text-gray-500">Notes:</div>
                      <div className="text-sm mt-1">{service.notes}</div>
                    </div>
                  )}
                </div>

                {canEdit && (
                  <div className="flex items-center space-x-2 ml-4">
                    {isExpoEligible(service) && (
                      <Button
                        type={expandedExpoForms.has(service.id) ? "primary" : "default"}
                        icon={<CalendarDays size={16} />}
                        onClick={() => toggleExpoForm(service.id)}
                        size="small"
                      >
                        {expandedExpoForms.has(service.id) ? "Hide Expo Days" : "Configure Expo Days"}
                      </Button>
                    )}
                    <Button
                      icon={<Edit size={16} />}
                      onClick={() => openEditService(service)}
                      size="small"
                    >
                      Edit
                    </Button>
                    <Button
                      icon={<Copy size={16} />}
                      onClick={() => handleOpenDuplicateModal(service)}
                      size="small"
                    >
                      Duplicate
                    </Button>
                    <Popconfirm
                      title="Delete Service"
                      description="Are you sure you want to delete this service?"
                      onConfirm={() => handleDelete(service.id)}
                      okText="Yes, Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        icon={<Trash2 size={16} />}
                        size="small"
                        loading={deleteServiceMutation.isPending}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  </div>
                )}
              </div>

              {/* Expo Service Form - shown when expanded */}
              {isExpoEligible(service) && expandedExpoForms.has(service.id) && (
                <div className="mt-4 pt-4 border-t">
                  <ExpoServiceForm
                    estimateServiceId={service.id}
                    onClose={() => toggleExpoForm(service.id)}
                  />
                </div>
              )}
            </Card>
            );
          })}
        </div>
      ) : (
        <Empty
          description="No services added yet"
          className="my-8"
        >
          {canEdit && (
            <Button type="primary" icon={<Plus size={16} />} onClick={openAddService}>
              Add First Service
            </Button>
          )}
        </Empty>
      )}

      {/* Duplicate Service Modal */}
      <DuplicateEstimateServiceModal
        service={serviceToDuplicate}
        open={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setServiceToDuplicate(null);
        }}
        onDuplicate={handleDuplicateService}
      />
    </div>
  );
};
